import { createSlice } from "@reduxjs/toolkit";
import {
  RenameFilesAction,
  MoveFileAction,
  UploadFilesAction,
} from "store/middlewares/files";
import {
  CreateShareLinkFileFolderAction,
  DeletePermissionsFileFolderAction,
  GetAllFoldersAction,
  GetChildFolderAction,
  GetFolderHierarchyAction,
  GetMoveFoldersAction,
  GetMyDriveAction,
  GetSharedDetailsAction,
  MoveFolderAction,
  MovesFileFolderAction,
  UpdateFolderAction,
} from "store/middlewares/folders";
import {
  GetAccessRequestAction,
  ActionAccessRequestAction,
} from "store/middlewares/general";
import { matchesDateAndLocalFilter } from "utils/filterUtils";

const sortItems = (items = [], sortBy, sortOrder, useFileNameOnly = false) => {
  if (!sortBy || !sortOrder) return items;

  // Normalise UI values ("newest" / "oldest") to the Redux convention
  const isDesc = sortOrder === "desc" || sortOrder === "newest";

  const getTimestamp = (item) => {
    const target = item?.file || item?.folder || item;
    let raw;

    switch (sortBy) {
      case "date_opened_by_me":
        raw = target?.last_opened_at;
        break;
      case "date_modified":
      case "date_modified_by_me":
        raw = target?.updated_at ?? target?.created_at;
        break;
      default:
        // fallback for any other date-based sort
        raw =
          target?.created_at ??
          target?.updated_at ??
          target?.date ??
          target?.last_opened_at;
    }

    if (raw === undefined || raw === null || raw === "") {
      return 0;
    }

    const num = Number(raw);
    if (!isNaN(num) && num > 0) {
      // 10-digit seconds → milliseconds
      return num < 1e11 ? num * 1000 : num;
    }

    return 0;
  };

  return [...items].sort((a, b) => {
    let comparison = 0;

    if (sortBy === "name") {
      const nameA = useFileNameOnly
        ? a?.file_name || ""
        : a?.name || a?.file_name || "";

      const nameB = useFileNameOnly
        ? b?.file_name || ""
        : b?.name || b?.file_name || "";

      comparison = nameA.localeCompare(nameB, undefined, {
        numeric: true,
        sensitivity: "base",
      });
    } else {
      comparison = getTimestamp(a) - getTimestamp(b);
    }

    return isDesc ? -comparison : comparison;
  });
};

export const foldersSlice = createSlice({
  name: "folders",
  initialState: {
    childFolders: [],
    childFoldersLoading: false,
    folders: null,
    foldersLoading: false,
    driveData: null,
    driveDataLoading: false,
    moveFolders: null,
    moveFoldersLoading: false,
    folderHierarchy: null,
    folderHierarchyLoading: false,
    sharedDetail: null,
    sharedDetailLoading: false,
    movingFolderLoading: false,
    accessRequest: null,
    accessRequestLoading: false,
    updateAccessRequestLoading: false,
    myDrive: null,
    myDriveLoading: false,
    sortOrder: {
      sortBy: "name",
      sortOrder: "asc",
    },
  },
  reducers: {
    setNullChildFolders: (state) => {
      state.childFolders = null;
    },
    updateFolderItems: (state, action) => {
      const payload = action.payload || {};
      const { id } = payload;
      const { sortBy, sortOrder } = state.sortOrder || {};

      if (!id) return;

      const folderLists = [
        state.driveData?.data?.folders,
        state.myDrive?.data?.folders,
        state.moveFolders?.data?.folders,
        state.folderHierarchy?.folders,
        state?.folderHierarchy,
      ];

      folderLists.forEach((folders) => {
        if (!Array.isArray(folders)) return;

        const folder = folders.find((f) => f.id === id);

        if (folder) {
          // Core display fields
          folder.name = payload.name ?? folder.name;
          folder.full_path = payload.full_path ?? folder.full_path;
          folder.folder_size = payload.folder_size ?? folder.folder_size;
          folder.file_size = payload.file_size ?? folder.file_size;

          // Timestamp fields – required for date_modified / date_opened sorting
          if (payload.updated_at !== undefined && payload.updated_at !== null) {
            folder.updated_at = payload.updated_at;
          }
          if (payload.created_at !== undefined && payload.created_at !== null) {
            folder.created_at = payload.created_at;
          }
          if (payload.last_opened_at !== undefined) {
            folder.last_opened_at = payload.last_opened_at;
          }

          // Optional extra fields that may arrive from the backend
          if (payload.is_starred !== undefined) {
            folder.is_starred = payload.is_starred;
          }
          if (payload.is_secure !== undefined) {
            folder.is_secure = payload.is_secure;
          }
          if (payload.general_access !== undefined) {
            folder.general_access = payload.general_access;
          }
          if (payload.permission !== undefined) {
            folder.permission = payload.permission;
          }
        }

        const sorted = sortItems(folders, sortBy, sortOrder);
        folders.length = 0;
        folders.push(...sorted);
      });
    },
    createFolder: (state, action) => {
      const newFolder = {
        ...action.payload,
        permission: action.payload.permission,
      };

      if (!newFolder?.id) return;

      // Always use the currently selected Redux sort order
      const { sortBy, sortOrder } = state.sortOrder || {};

      // Current folder / Drive data
      if (state.driveData?.data) {
        const folders = state.driveData.data.folders || [];

        const alreadyExists = folders.some(
          (folder) => folder.id === newFolder.id,
        );

        if (
          !alreadyExists &&
          state.driveData?.folder?.id === newFolder?.parent_id &&
          matchesDateAndLocalFilter(newFolder, state.driveData?.activeFilter)
        ) {
          state.driveData.data.folders = sortItems(
            [...folders, newFolder],
            sortBy,
            sortOrder,
          );
        }
      }

      // My Drive / root folder
      if (state.myDrive?.data) {
        const folders = state.myDrive.data.folders || [];

        const alreadyExists = folders.some(
          (folder) => folder.id === newFolder.id,
        );

        if (
          !alreadyExists &&
          newFolder?.parent_id === null &&
          matchesDateAndLocalFilter(newFolder, state.myDrive?.activeFilter)
        ) {
          state.myDrive.data.folders = sortItems(
            [...folders, newFolder],
            sortBy,
            sortOrder,
          );
        }
      }
    },

    updateSortOrder: (state, action) => {
      const { sortBy, sortOrder } = action.payload;
      state.sortOrder = { sortBy, sortOrder };
    },
    renameFile: (state, action) => {
      const updatedFile = action.payload;

      [state.driveData?.data?.files, state.myDrive?.data?.files]
        .filter(Boolean)
        .forEach((list) => {
          const index = list.findIndex((f) => f?.id === updatedFile?.id);

          if (index !== -1) {
            list[index] = updatedFile;
          }
        });
    },
    uploadfiles: (state, action) => {
      const newFiles = action.payload?.uploaded || [];

      if (newFiles.length === 0) return;
      const { sortBy, sortOrder } = state.sortOrder || {};

      // Update Folder Page data
      if (state.driveData?.folder?.id === newFiles[0].folder_id) {
        const validNewFiles = newFiles.filter((file) =>
          matchesDateAndLocalFilter(file, state.driveData?.activeFilter),
        );
        let updatedFiles = sortItems(
          [...validNewFiles, ...(state.driveData?.data?.files || [])],
          sortBy,
          sortOrder,
          true,
        );

        state.driveData = {
          ...state.driveData,
          data: {
            folders: [...(state.driveData?.data?.folders || [])],
            files: updatedFiles,
          },
        };
      }

      // Update My Drive data
      if (newFiles[0].folder_id === null) {
        const validNewFiles = newFiles.filter((file) =>
          matchesDateAndLocalFilter(file, state.myDrive?.activeFilter),
        );
        let updatedFiles = sortItems(
          [...validNewFiles, ...(state.myDrive?.data?.files || [])],
          sortBy,
          sortOrder,
          true,
        );

        state.myDrive = {
          ...state.myDrive,
          data: {
            folders: [...(state.myDrive?.data?.folders || [])],
            files: updatedFiles,
          },
        };
      }
    },
    uploadFolder: (state, action) => {
      const newFolder = action.payload || {};

      if (newFolder) {
        const { sortBy, sortOrder } = state.sortOrder || {};

        // Update Folder Page data
        if (
          state.driveData?.folder?.id === newFolder?.parent_id &&
          matchesDateAndLocalFilter(newFolder, state.driveData?.activeFilter)
        ) {
          let updatedFolders = sortItems(
            [newFolder, ...(state.driveData?.data?.folders || [])],
            sortBy,
            sortOrder,
            false, // folders do not use file_name
          );

          state.driveData = {
            ...state.driveData,
            data: {
              folders: updatedFolders,
              files: [...(state.driveData?.data?.files || [])],
            },
          };
        }

        // Update My Drive data
        if (
          newFolder?.parent_id === null &&
          matchesDateAndLocalFilter(newFolder, state.myDrive?.activeFilter)
        ) {
          let updatedFolders = sortItems(
            [newFolder, ...(state.myDrive?.data?.folders || [])],
            sortBy,
            sortOrder,
            false, // folders do not use file_name
          );

          state.myDrive = {
            ...state.myDrive,
            data: {
              folders: updatedFolders,
              files: [...(state.myDrive?.data?.files || [])],
            },
          };
        }
      }
    },
    trashFilesAndFolders: (state, action) => {
      const trashedFolders = action.payload?.folders || [];
      const trashedFiles = action.payload?.files || [];

      const trashedFolderIds = trashedFolders.map((f) => f.id);
      const trashedFileIds = trashedFiles.map((f) => f.id);

      // Update FolderPage data
      if (state.driveData?.data) {
        state.driveData = {
          ...state.driveData,
          data: {
            folders: state.driveData.data.folders?.filter(
              (folder) => !trashedFolderIds.includes(folder.id),
            ),
            files: state.driveData.data.files?.filter(
              (file) => !trashedFileIds.includes(file.id),
            ),
          },
        };
      }

      // Update MyDrive data
      if (state.myDrive?.data) {
        state.myDrive = {
          ...state.myDrive,
          data: {
            folders: state.myDrive.data.folders?.filter(
              (folder) => !trashedFolderIds.includes(folder.id),
            ),
            files: state.myDrive.data.files?.filter(
              (file) => !trashedFileIds.includes(file.id),
            ),
          },
        };
      }
    },
    moveFilesAndFolders: (state, action) => {
      const response = action.payload || {};

      const movedFolders = response.folders || [];
      const movedFiles = response.files || [];
      const newParentId = response.new_parent;

      const movedFolderIds = movedFolders.map((folder) => folder.id);
      const movedFileIds = movedFiles.map((file) => file.id);

      const { sortBy, sortOrder } = state.sortOrder || {};

      const updateDriveData = (drive) => {
        if (!drive?.data) return drive;

        let updatedFiles = drive.data.files?.filter(
          (item) => !movedFileIds.includes(item.id),
        );

        let updatedFolders = drive.data.folders?.filter(
          (item) => !movedFolderIds.includes(item.id),
        );

        const validMovedFiles = movedFiles.filter((item) =>
          matchesDateAndLocalFilter(item, drive?.activeFilter),
        );
        const validMovedFolders = movedFolders.filter((item) =>
          matchesDateAndLocalFilter(item, drive?.activeFilter),
        );

        // Add moved items to the destination folder.
        if (drive.folder?.id === newParentId) {
          updatedFiles = sortItems(
            [...updatedFiles, ...validMovedFiles],
            sortBy,
            sortOrder,
          );

          updatedFolders = sortItems(
            [...updatedFolders, ...validMovedFolders],
            sortBy,
            sortOrder,
          );
        }

        // If destination is My Drive/root.
        if (newParentId === null && drive === state.myDrive) {
          updatedFiles = sortItems(
            [...updatedFiles, ...validMovedFiles],
            sortBy,
            sortOrder,
          );

          updatedFolders = sortItems(
            [...updatedFolders, ...validMovedFolders],
            sortBy,
            sortOrder,
          );
        }

        return {
          ...drive,
          data: {
            ...drive.data,
            files: updatedFiles,
            folders: updatedFolders,
          },
        };
      };

      state.driveData = updateDriveData(state.driveData);
      state.myDrive = updateDriveData(state.myDrive);
    },
    restoreFilesAndFolder: (state, action) => {
      const response = action.payload || {};

      const restoredFolders = response.folders || [];
      const restoredFiles = response.files || [];

      const { sortBy, sortOrder } = state.sortOrder || {};

      const restoredFileIds = restoredFiles.map((file) => file.id);
      const restoredFolderIds = restoredFolders.map((folder) => folder.id);

      const updateDriveData = (drive) => {
        if (!drive?.data) return drive;

        let updatedFiles = (drive.data.files || []).filter(
          (item) => !restoredFileIds.includes(item.id),
        );

        let updatedFolders = (drive.data.folders || []).filter(
          (item) => !restoredFolderIds.includes(item.id),
        );

        const filesForThisDrive = restoredFiles.filter((file) => {
          const folderId = file?.folder_id ?? null;

          if (folderId === null) {
            return (
              drive === state.myDrive &&
              matchesDateAndLocalFilter(file, drive?.activeFilter)
            );
          }

          return (
            drive.folder?.id === folderId &&
            matchesDateAndLocalFilter(file, drive?.activeFilter)
          );
        });

        const foldersForThisDrive = restoredFolders.filter((folder) => {
          const parentId = folder?.parent_id ?? null;

          if (parentId === null) {
            return (
              drive === state.myDrive &&
              matchesDateAndLocalFilter(folder, drive?.activeFilter)
            );
          }

          return (
            drive.folder?.id === parentId &&
            matchesDateAndLocalFilter(folder, drive?.activeFilter)
          );
        });

        if (filesForThisDrive.length) {
          updatedFiles = sortItems(
            [...updatedFiles, ...filesForThisDrive],
            sortBy,
            sortOrder,
          );
        }

        if (foldersForThisDrive.length) {
          updatedFolders = sortItems(
            [...updatedFolders, ...foldersForThisDrive],
            sortBy,
            sortOrder,
          );
        }

        return {
          ...drive,
          data: {
            ...drive.data,
            files: updatedFiles,
            folders: updatedFolders,
          },
        };
      };

      state.driveData = updateDriveData(state.driveData);
      state.myDrive = updateDriveData(state.myDrive);
    },

    starUnstarItems: (state, action) => {
      const { files = [], folders = [] } = action.payload || {};

      const applyStarStatus = (list, items) => {
        if (!Array.isArray(list) || !items.length) return;

        items.forEach((item) => {
          const target = list.find((entry) => entry?.id === item?.id);
          if (target && item.is_starred !== undefined) {
            target.is_starred = item.is_starred;
          }
        });
      };

      // Update FolderPage data
      if (state.driveData?.data) {
        applyStarStatus(state.driveData.data.folders, folders);
        applyStarStatus(state.driveData.data.files, files);
      }

      // Update MyDrive data
      if (state.myDrive?.data) {
        applyStarStatus(state.myDrive.data.folders, folders);
        applyStarStatus(state.myDrive.data.files, files);
      }
    },

    lockUnlockFileAndFolder: (state, action) => {
      const { files = [], folders = [] } = action.payload || {};

      const fileIds = files?.map((f) => f.id);
      const folderIds = folders?.map((f) => f.id);

      // Update FolderPage data
      if (state.driveData?.data) {
        state.driveData.data.folders?.forEach((folder) => {
          if (folderIds.includes(folder.id)) {
            folder.is_secure = !folder.is_secure;
          }
        });

        state.driveData.data.files?.forEach((file) => {
          if (fileIds.includes(file.id)) {
            file.is_secure = !file.is_secure;
          }
        });
      }

      // Update MyDrive data
      if (state.myDrive?.data) {
        state.myDrive.data.folders?.forEach((folder) => {
          if (folderIds.includes(folder.id)) {
            folder.is_secure = !folder.is_secure;
          }
        });

        state.myDrive.data.files?.forEach((file) => {
          if (fileIds.includes(file.id)) {
            file.is_secure = !file.is_secure;
          }
        });
      }
    },
    updateFileFolderPermission: (state, action) => {
      const response = action.payload || {};

      const { file_id, folder_id, user_id, permission, profileId } = response;

      if (!permission || !user_id || !profileId || user_id !== profileId) {
        return;
      }

      const updatePermission = (items = []) => {
        items.forEach((item) => {
          if (
            (file_id && item.id === file_id) ||
            (folder_id && item.id === folder_id)
          ) {
            item.permission = permission;
          }
        });
      };

      updatePermission(state.driveData?.data?.files);
      updatePermission(state.driveData?.data?.folders);

      updatePermission(state.myDrive?.data?.files);
      updatePermission(state.myDrive?.data?.folders);
    },
    removeFileAndFolderPemission: (state, action) => {
      const response = action.payload || {};

      const { profileId, user_id, folder_id, file_id } = response;

      // Only remove the item if permission was removed
      // for the currently logged-in user.
      if (!profileId || !user_id || user_id !== profileId) {
        return;
      }

      // Remove from direct file/folder lists
      const directLists = [
        state.driveData?.data?.files,
        state.driveData?.data?.folders,
        state.myDrive?.data?.files,
        state.myDrive?.data?.folders,
      ];

      directLists.forEach((list) => {
        if (!list) return;

        const filteredList = list.filter(
          (item) =>
            !(
              (file_id && item?.id === file_id) ||
              (folder_id && item?.id === folder_id)
            ),
        );

        list.length = 0;
        list.push(...filteredList);
      });

      // If the currently open folder is the one whose permission was removed → clear it
      if (folder_id && state.driveData?.folder?.id === folder_id) {
        state.driveData = null;
        state.folderHierarchy = null;
      }
    },
    updateUserProfile: (state, action) => {
      const { id } = action.payload;
      if (state.sharedDetail) {
        state.sharedDetail.shared_users = state.sharedDetail.shared_users.map(
          (user) => {
            if (user.id === id) {
              return { ...user, ...action.payload };
            }
            return user;
          },
        );
        if (state.sharedDetail?.owner?.id === id) {
          state.sharedDetail.owner = {
            ...state.sharedDetail.owner,
            ...action.payload,
          };
        }
      }
    },
    createAccessRequest: (state, action) => {
      const response = action.payload || {};

      const profileId = response.profileId; // logged-in user id
      const ownerId = response.owner_id;

      // Only the owner of the file/folder should see the access request
      if (!profileId || !ownerId || profileId !== ownerId) {
        return;
      }

      // Ensure accessRequest is always an array
      if (!Array.isArray(state.accessRequest)) {
        state.accessRequest = [];
      }

      // Prevent duplicates
      const alreadyExists = state.accessRequest.some(
        (item) => item.id === response.id,
      );

      if (!alreadyExists) {
        // Add the new request at the beginning
        state.accessRequest.unshift(response);
      }
    },
    acceptAccessRequest: (state, action) => {
      const response = action.payload || {};

      const profileId = response.profileId; // logged-in user id
      const ownerId = response.owner_id;
      const requestId = response.id;
      const requester = response.requester;

      // Only the owner should process this
      if (!profileId || !ownerId || profileId !== ownerId) {
        return;
      }

      // 1. Remove the request from accessRequest list
      if (Array.isArray(state.accessRequest)) {
        state.accessRequest = state.accessRequest.filter(
          (item) => item.id !== requestId,
        );
      }

      // 2. Add the requester into sharedDetail.shared_users
      if (state.sharedDetail && requester?.id) {
        // Ensure shared_users exists
        if (!Array.isArray(state.sharedDetail.shared_users)) {
          state.sharedDetail.shared_users = [];
        }

        // Prevent duplicate
        const alreadyExists = state.sharedDetail.shared_users.some(
          (user) => user.id === requester.id,
        );

        if (!alreadyExists) {
          state.sharedDetail.shared_users.push({
            id: requester.id,
            email: requester.email,
            first_name: requester.first_name,
            last_name: requester.last_name,
            permission: response.permission || "view",
            // add any other fields you normally keep in shared_users
          });
        }
      }
    },
    declineAccessRequest: (state, action) => {
      const response = action.payload || {};

      const profileId = response.profileId; // logged-in user id
      const ownerId = response.owner_id;
      const requestId = response.id;

      // Only the owner should process this
      if (!profileId || !ownerId || profileId !== ownerId) {
        return;
      }

      // Remove the request from accessRequest list
      if (Array.isArray(state.accessRequest)) {
        state.accessRequest = state.accessRequest.filter(
          (item) => item.id !== requestId,
        );
      }
    },
    updateRecentFolder: (state, action) => {
      const { id, last_opened_at } = action.payload || {};
      if (!id) return;

      const openedAt = last_opened_at;
      const { sortBy, sortOrder } = state.sortOrder || {};
      const shouldResort = sortBy === "date_opened_by_me";

      [
        state.driveData?.data,
        state.myDrive?.data,
        state.moveFolders?.data,
      ].forEach((data) => {
        if (!data || !Array.isArray(data.folders)) return;

        const folder = data.folders.find((f) => f?.id === id);
        if (!folder) return;

        folder.last_opened_at = openedAt;

        if (shouldResort) {
          data.folders = sortItems(data.folders, sortBy, sortOrder);
        }
      });
    },
    updateRecentFile: (state, action) => {
      const { id, last_opened_at } = action.payload || {};
      if (!id) return;

      const openedAt = last_opened_at;
      const { sortBy, sortOrder } = state.sortOrder || {};
      const shouldResort = sortBy === "date_opened_by_me";

      [state.driveData?.data, state.myDrive?.data].forEach((data) => {
        if (!data || !Array.isArray(data.files)) return;

        const file = data.files.find((f) => f?.id === id);
        if (!file) return;

        file.last_opened_at = openedAt;

        if (shouldResort) {
          data.files = sortItems(data.files, sortBy, sortOrder, true);
        }
      });
    },
    updateSharedDetailPermission: (state, action) => {
      const response = action.payload || {};
      const { user_id, permission } = response;

      if (!user_id || !permission) return;
      if (!state.sharedDetail?.shared_users) return;

      state.sharedDetail.shared_users = state.sharedDetail.shared_users.map(
        (user) => {
          if (user.id === user_id) {
            return {
              ...user,
              role: permission,
            };
          }
          return user;
        },
      );
    },

    removeSharedDetailPermission: (state, action) => {
      const response = action.payload || {};
      const userId = response.user_id || response.user?.id;

      if (!userId) return;
      if (!state.sharedDetail?.shared_users) return;

      // 1. Remove the user from shared_users
      state.sharedDetail.shared_users = state.sharedDetail.shared_users.filter(
        (user) => user.id !== userId,
      );

      // 2. Update is_shared on the file / folder in driveData & myDrive
      //    Prefer the value that arrives from the backend.
      const isShared = response.is_shared;

      if (isShared === undefined) return;

      const fileId = response.file?.id || response.file_id || null;
      const folderId = response.folder?.id || response.folder_id || null;

      if (!fileId && !folderId) return;

      const updateIsShared = (list = []) => {
        if (!Array.isArray(list)) return;
        list.forEach((item) => {
          if (
            (fileId && item.id === fileId) ||
            (folderId && item.id === folderId)
          ) {
            item.is_shared = isShared;
          }
        });
      };

      // Folder page
      updateIsShared(state.driveData?.data?.files);
      updateIsShared(state.driveData?.data?.folders);

      // My Drive / root
      updateIsShared(state.myDrive?.data?.files);
      updateIsShared(state.myDrive?.data?.folders);
    },

    /**
     * Add a new shared user to sharedDetail.shared_users
     * (when a new permission is created / share is accepted)
     */
    createSharedDetailPermission: (state, action) => {
      const response = action.payload || {};
      const permissions = response.permissions || [];

      if (!permissions.length) return;

      // ------------------------------------------------------------------
      // 1. Ensure sharedDetail exists and add the new shared users
      // ------------------------------------------------------------------
      if (!state.sharedDetail) {
        state.sharedDetail = {
          owner: response.owner || null,
          shared_users: [],
          file: response.file || null,
          folder: response.folder || null,
        };
      }
      if (!Array.isArray(state.sharedDetail.shared_users)) {
        state.sharedDetail.shared_users = [];
      }

      permissions.forEach((perm) => {
        const user = perm.user || {};
        const userId = perm.user_id || user.id;
        if (!userId) return;

        const alreadyExists = state.sharedDetail.shared_users.some(
          (u) => u.id === userId,
        );
        if (alreadyExists) return;

        state.sharedDetail.shared_users.push({
          id: userId,
          first_name: user.first_name || "",
          last_name: user.last_name || "",
          email: user.email || "",
          role: perm.permission || "view",
          image: user.image || null,
          image_url: user.image_url || null,
          resized_image: user.resized_image || null,
          resized_image_urls: user.resized_image_urls || null,
        });
      });

      // Keep owner / file / folder references if they were missing
      if (response.owner && !state.sharedDetail.owner) {
        state.sharedDetail.owner = response.owner;
      }
      if (response.file && !state.sharedDetail.file) {
        state.sharedDetail.file = response.file;
      }
      if (response.folder && !state.sharedDetail.folder) {
        state.sharedDetail.folder = response.folder;
      }

      // ------------------------------------------------------------------
      // 2. Propagate is_shared to the actual file / folder objects
      // ------------------------------------------------------------------
      // Prefer the value that arrives from the backend; fall back to true
      // because a permission was just created.
      const isShared = response.file?.is_shared ?? response.folder?.is_shared;

      const fileId = response.file?.id || response.file_id || null;
      const folderId = response.folder?.id || response.folder_id || null;

      if (!fileId && !folderId) return; // nothing to update in the lists

      const updateIsShared = (list = []) => {
        if (!Array.isArray(list)) return;
        list.forEach((item) => {
          if (
            (fileId && item.id === fileId) ||
            (folderId && item.id === folderId)
          ) {
            item.is_shared = isShared;
          }
        });
      };

      // Folder page
      updateIsShared(state.driveData?.data?.files);
      updateIsShared(state.driveData?.data?.folders);

      // My Drive / root
      updateIsShared(state.myDrive?.data?.files);
      updateIsShared(state.myDrive?.data?.folders);
    },
    removeDriveData: (state) => {
      state.driveData = null;
    },
  },
  extraReducers(builder) {
    builder
      // Get child folders
      .addCase(GetChildFolderAction.pending, (state) => {
        state.childFoldersLoading = true;
      })
      .addCase(GetChildFolderAction.fulfilled, (state, action) => {
        state.childFolders = action.payload.response;
        state.childFoldersLoading = false;
      })
      .addCase(GetChildFolderAction.rejected, (state) => {
        state.childFoldersLoading = false;
      })

      .addCase(GetAllFoldersAction.pending, (state) => {
        state.driveDataLoading = state.driveData?.total > 0 ? false : true;
      })
      .addCase(GetAllFoldersAction.fulfilled, (state, action) => {
        state.driveDataLoading = false;
        const newData = action.payload?.response;
        const { sortBy, sortOrder } = action.meta.arg || {};

        if (newData.page === 1) {
          state.driveData = {
            ...newData,
            activeFilter: action.meta.arg,
          };
        } else {
          state.driveData = {
            ...newData,
            activeFilter: action.meta.arg,
            data: {
              folders: sortItems(
                [
                  ...state.driveData.data.folders,
                  ...(newData?.data?.folders || []),
                ],
                sortBy,
                sortOrder,
              ),
              files: sortItems(
                [
                  ...state.driveData.data.files,
                  ...(newData?.data?.files || []),
                ],
                sortBy,
                sortOrder,
              ),
            },
          };
        }
      })
      .addCase(GetAllFoldersAction.rejected, (state) => {
        state.driveDataLoading = false;
      })

      .addCase(GetMyDriveAction.pending, (state) => {
        state.myDriveLoading = state.myDrive?.total > 0 ? false : true;
      })
      .addCase(GetMyDriveAction.fulfilled, (state, action) => {
        state.myDriveLoading = false;
        const newData = action.payload?.response;
        const { sortBy, sortOrder } = action.meta.arg || {};

        if (newData.page === 1) {
          state.myDrive = {
            ...newData,
            activeFilter: action.meta.arg,
          };
        } else {
          if (action.payload?.isLoadMore) {
            state.myDrive = {
              ...newData,
              activeFilter: action.meta.arg,
              data: {
                folders: sortItems(
                  [
                    ...state.myDrive.data.folders,
                    ...(newData?.data?.folders || []),
                  ],
                  sortBy,
                  sortOrder,
                ),
                files: sortItems(
                  [
                    ...state.myDrive.data.files,
                    ...(newData?.data?.files || []),
                  ],
                  sortBy,
                  sortOrder,
                ),
              },
            };
          } else {
            state.myDrive = {
              ...newData,
              activeFilter: action.meta.arg,
            };
          }
        }
      })
      .addCase(GetMyDriveAction.rejected, (state) => {
        state.myDriveLoading = false;
      })

      .addCase(GetMoveFoldersAction.pending, (state) => {
        state.moveFoldersLoading = state.moveFolders?.total > 0 ? false : true;
      })
      .addCase(GetMoveFoldersAction.fulfilled, (state, action) => {
        state.moveFoldersLoading = false;
        const newData = action.payload?.response;
        const { sortBy, sortOrder } = action.meta.arg || {};

        if (newData.page === 1) {
          state.moveFolders = newData;
        } else {
          state.moveFolders = {
            ...newData,
            data: {
              folders: sortItems(
                [
                  ...state.moveFolders.data.folders,
                  ...(newData?.data?.folders || []),
                ],
                sortBy,
                sortOrder,
              ),
              // files: [
              //   ...state.moveFolders.data.files,
              //   ...(newData?.data?.files || []),
              // ],
            },
          };
        }
      })
      .addCase(GetMoveFoldersAction.rejected, (state) => {
        state.moveFoldersLoading = false;
      })
      // Get folder hierarchy
      .addCase(GetFolderHierarchyAction.pending, (state) => {
        state.folderHierarchyLoading = true;
      })
      .addCase(GetFolderHierarchyAction.fulfilled, (state, action) => {
        state.folderHierarchy = action.payload.response;
        state.folderHierarchyLoading = false;
      })
      .addCase(GetFolderHierarchyAction.rejected, (state) => {
        state.folderHierarchyLoading = false;
      })
      .addCase(UploadFilesAction.fulfilled, (state, action) => {
        const { oldFileId } = action.payload || {};
        if (oldFileId) {
          // Update FolderPage data
          if (state.driveData?.data) {
            state.driveData = {
              ...state.driveData,
              data: {
                folders: state.driveData.data.folders?.filter(
                  (folder) => folder.id !== oldFileId,
                ),
                files: state.driveData.data.files?.filter(
                  (file) => file.id !== oldFileId,
                ),
              },
            };
          }

          // Update MyDrive data
          if (state.myDrive?.data) {
            state.myDrive = {
              ...state.myDrive,
              data: {
                folders: state.myDrive.data.folders?.filter(
                  (folder) => folder.id !== oldFileId,
                ),
                files: state.myDrive.data.files?.filter(
                  (file) => file.id !== oldFileId,
                ),
              },
            };
          }
        }
      })

      // update folder
      .addCase(UpdateFolderAction.fulfilled, (state, action) => {
        const updatedFolder = action.payload.response;

        const driveFolder = state.driveData?.data?.folders?.find(
          (f) => f.id === updatedFolder.id,
        );
        if (driveFolder) {
          driveFolder.name = updatedFolder.name;
          driveFolder.full_path = updatedFolder.full_path;
        }

        const myDriveFolder = state.myDrive?.data?.folders?.find(
          (f) => f.id === updatedFolder.id,
        );
        if (myDriveFolder) {
          myDriveFolder.name = updatedFolder.name;
          myDriveFolder.full_path = updatedFolder.full_path;
        }
      })

      .addCase(RenameFilesAction.fulfilled, (state, action) => {
        const updatedFile = action.payload.response;

        [
          state.driveData?.data?.files,
          state.myDrive?.data?.files,
          state.folderDetails?.files,
        ]
          .filter(Boolean)
          .forEach((list) => {
            const index = list.findIndex((f) => f.id === updatedFile.id);

            if (index !== -1) {
              list[index] = updatedFile;
            }
          });
      })

      .addCase(MoveFolderAction.pending, (state) => {
        state.movingFolderLoading = true;
      })
      .addCase(MoveFolderAction.fulfilled, (state, action) => {
        state.movingFolderLoading = false;

        if (state.driveData?.data) {
          state.driveData = {
            ...state.driveData,
            data: {
              ...state.driveData.data,
              folders: state.driveData.data.folders?.filter(
                (item) => item.id !== action.payload.folderId,
              ),
            },
          };
        }

        if (state.myDrive?.data) {
          state.myDrive = {
            ...state.myDrive,
            data: {
              ...state.myDrive.data,
              folders: state.myDrive.data.folders?.filter(
                (item) => item.id !== action.payload.folderId,
              ),
            },
          };
        }
      })
      .addCase(MoveFolderAction.rejected, (state) => {
        state.movingFolderLoading = false;
      })
      .addCase(MovesFileFolderAction.pending, (state) => {
        state.movingFolderLoading = true;
      })
      .addCase(MovesFileFolderAction.fulfilled, (state) => {
        state.movingFolderLoading = false;
      })
      .addCase(MovesFileFolderAction.rejected, (state) => {
        state.movingFolderLoading = false;
      })

      // Move file To another folder
      .addCase(MoveFileAction.fulfilled, (state, action) => {
        if (state.driveData?.data) {
          state.driveData = {
            ...state.driveData,
            data: {
              ...state.driveData.data,
              files: state.driveData.data.files?.filter(
                (item) => item.id !== action.payload.fileId,
              ),
            },
          };
        }

        if (state.myDrive?.data) {
          state.myDrive = {
            ...state.myDrive,
            data: {
              ...state.myDrive.data,
              files: state.myDrive.data.files?.filter(
                (item) => item.id !== action.payload.fileId,
              ),
            },
          };
        }
      })

      // Get shared detail
      .addCase(GetSharedDetailsAction.pending, (state) => {
        state.sharedDetailLoading = true;
      })
      .addCase(GetSharedDetailsAction.fulfilled, (state, action) => {
        state.sharedDetail = action.payload.response;
        state.sharedDetailLoading = false;
      })
      .addCase(GetSharedDetailsAction.rejected, (state) => {
        state.sharedDetailLoading = false;
      })

      .addCase(DeletePermissionsFileFolderAction.fulfilled, (state, action) => {
        const deletedId = action.payload.response.user_id;

        state.sharedDetail = {
          ...state.sharedDetail,
          shared_users: state.sharedDetail?.shared_users?.filter(
            (item) => item.id !== deletedId,
          ),
        };
      })

      .addCase(CreateShareLinkFileFolderAction.fulfilled, (state, action) => {
        const { shared_link, access_link } = action.payload.response;

        const folderId = action.meta.arg.folderId;
        const fileId = action.meta.arg.fileId;

        const generalAccess = {
          visibility: shared_link.visibility,
          role: shared_link.role,
          shared_link: access_link,
          expires_at: shared_link.expires_at,
        };

        // Update driveData folders
        if (folderId && state.driveData?.data?.folders) {
          const folder = state.driveData.data.folders.find(
            (item) => item.id === folderId,
          );

          if (folder) {
            folder.general_access = generalAccess;
          }
        }

        // Update driveData files
        if (fileId && state.driveData?.data?.files) {
          const file = state.driveData.data.files.find(
            (item) => item.id === fileId,
          );

          if (file) {
            file.general_access = generalAccess;
          }
        }

        // Update myDrive folders
        if (folderId && state.myDrive?.data?.folders) {
          const folder = state.myDrive.data.folders.find(
            (item) => item.id === folderId,
          );

          if (folder) {
            folder.general_access = generalAccess;
          }
        }

        // Update myDrive files
        if (fileId && state.myDrive?.data?.files) {
          const file = state.myDrive.data.files.find(
            (item) => item.id === fileId,
          );

          if (file) {
            file.general_access = generalAccess;
          }
        }

        // Update modal data immediately
        if (state.sharedDetail) {
          state.sharedDetail.general_access = generalAccess;
        }
      })

      .addCase(GetAccessRequestAction.pending, (state) => {
        state.accessRequestLoading = true;
      })
      .addCase(GetAccessRequestAction.fulfilled, (state, action) => {
        state.accessRequest = action.payload.response;
        state.accessRequestLoading = false;
      })
      .addCase(GetAccessRequestAction.rejected, (state) => {
        state.accessRequestLoading = false;
      })

      .addCase(ActionAccessRequestAction.pending, (state) => {
        state.updateAccessRequestLoading = true;
      })
      .addCase(ActionAccessRequestAction.fulfilled, (state, action) => {
        const updatedAccessRequest = action.payload.response;
        state.accessRequest = state.accessRequest?.map((item) =>
          item.id === updatedAccessRequest.id
            ? { ...item, status: updatedAccessRequest.status }
            : item,
        );
        state.updateAccessRequestLoading = false;
      })
      .addCase(ActionAccessRequestAction.rejected, (state) => {
        state.updateAccessRequestLoading = false;
      });
  },
});

export const {
  setNullChildFolders,
  updateFolderItems,
  createFolder,
  updateSortOrder,
  renameFile,
  uploadfiles,
  trashFilesAndFolders,
  uploadFolder,
  moveFilesAndFolders,
  restoreFilesAndFolder,
  starUnstarItems,
  lockUnlockFileAndFolder,
  updateFileFolderPermission,
  removeFileAndFolderPemission,
  updateUserProfile,
  createAccessRequest,
  acceptAccessRequest,
  declineAccessRequest,
  updateSharedDetailPermission,
  removeSharedDetailPermission,
  createSharedDetailPermission,
  removeDriveData,
  updateRecentFolder,
  updateRecentFile
} = foldersSlice.actions;

export default foldersSlice.reducer;